import {
  ActionSchema,
  DynamicStringSchema,
  DynamicValueSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import {
  CreateQuoteRow,
  type QuoteStatusError,
  type QuoteStatusResult,
} from './CreateQuoteRow';
import type { AutoRefreshMode } from './useAutoRefresh';

interface ResolvedProps {
  readonly label?: string;
  readonly limit?: string;
  readonly retention?: string;
  readonly parts?: string;
  readonly price?: string;
  readonly status?: string;
  readonly quoteNumber?: string;
  readonly statusReasons?: unknown;
  readonly errorReason?: string;
  readonly autoRefresh?: string;
  // Written by A2UISurfaceList after RefreshQuoteStatus settles, at
  // `/results/<this component's id>`; never sent by the agent.
  readonly result?: QuoteStatusResult | QuoteStatusError;
  readonly onOpenReview?: () => void;
  readonly onRefresh?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  label: DynamicStringSchema,
  limit: DynamicStringSchema,
  retention: DynamicStringSchema,
  parts: DynamicStringSchema,
  price: DynamicStringSchema,
  status: DynamicStringSchema,
  quoteNumber: DynamicStringSchema,
  statusReasons: DynamicValueSchema,
  errorReason: DynamicStringSchema,
  autoRefresh: DynamicStringSchema,
  result: DynamicValueSchema,
  onOpenReview: ActionSchema,
  onRefresh: ActionSchema,
}) as any;

/** Keeps only string reasons; anything else is dropped rather than crashing. */
function toReasons(value: unknown): readonly string[] | undefined {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : undefined;
}

/** Unknown modes fall back to manual-only rather than guessing a cadence. */
function toAutoRefresh(value: unknown): AutoRefreshMode | undefined {
  return value === 'once' || value === 'poll' ? value : undefined;
}

export const CreateQuoteRowImpl = createComponentImplementation(
  { name: 'CreateQuoteRow', schema },
  ({
    props,
  }: {
    props: ResolvedProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildChild: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
  }) => (
    <CreateQuoteRow
      label={props.label ?? ''}
      limit={props.limit ?? ''}
      retention={props.retention ?? ''}
      parts={props.parts}
      price={props.price ?? ''}
      status={props.status}
      quoteNumber={props.quoteNumber}
      statusReasons={toReasons(props.statusReasons)}
      errorReason={props.errorReason}
      autoRefresh={toAutoRefresh(props.autoRefresh)}
      result={props.result}
      onOpenReview={props.onOpenReview}
      onRefresh={props.onRefresh}
    />
  )
);
